import { useCreateStudent } from '@/hooks/useCreateStudent';
import { useLocale } from '@/providers/LocaleProvider';
import { CreateStudentForm, createStudentSchema } from '@/utilities/createStudentSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { ConfirmationDialog } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TextField } from '../TextField';
import { Dialog } from './Dialog';

const defaultValues: CreateStudentForm = {
  firstName: '',
  lastName: '',
  email: '',
};

interface CreateStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (student: { employeeId: string; fullName: string }) => void;
  initialName?: string;
}

export const CreateStudentDialog = ({
  open,
  onClose,
  onCreated,
  initialName,
}: CreateStudentDialogProps) => {
  const { translate } = useLocale();
  const { mutate: createStudent, isPending } = useCreateStudent();

  const {
    control,
    handleSubmit,
    formState: { isValid, dirtyFields },
    reset,
  } = useForm<CreateStudentForm>({
    defaultValues,
    resolver: zodResolver(createStudentSchema),
    mode: 'onChange',
  });

  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const isFormDirty = !isEmpty(dirtyFields);

  const handleClose = () => {
    reset(defaultValues);
    setShowUnsavedWarning(false);
    onClose();
  };

  const onShowWarnCloseDialog = () => {
    if (isFormDirty) return setShowUnsavedWarning(true);
    handleClose();
  };

  const onSubmit = (data: CreateStudentForm) => {
    createStudent(
      {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
      },
      {
        onSuccess: (response) => {
          onCreated?.({
            employeeId: response.data.employeeId,
            fullName: `${data.firstName.trim()} ${data.lastName.trim()}`,
          });
          handleClose();
        },
      }
    );
  };

  useEffect(() => {
    if (initialName) {
      const [firstName, ...lastNameParts] = initialName.split(' ');

      reset({
        firstName,
        lastName: lastNameParts.join(' '),
        email: '',
      });
    }
  }, [initialName, reset]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onShowWarnCloseDialog}
        title={translate('manage.createStudent.title')}
        maxWidth='sm'
        actions={[
          {
            label: translate('manage.createStudent.create'),
            variant: 'filled',
            form: 'create-student-form',
            disabled: !isValid || isPending,
            isLoading: isPending,
          },
          {
            label: translate('common.cancel'),
            onClick: onShowWarnCloseDialog,
            disabled: isPending,
          },
        ]}
      >
        <Stack
          component='form'
          id='create-student-form'
          onSubmit={handleSubmit(onSubmit)}
          spacing={3}
        >
          <Controller
            control={control}
            name='firstName'
            render={({ field, fieldState }) => (
              <TextField
                label={translate('manage.createStudent.firstName')}
                fullWidth
                clearable={false}
                autoComplete={false}
                error={!!fieldState.error}
                helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name='lastName'
            render={({ field, fieldState }) => (
              <TextField
                label={translate('manage.createStudent.lastName')}
                fullWidth
                clearable={false}
                autoComplete={false}
                error={!!fieldState.error}
                helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name='email'
            render={({ field, fieldState }) => (
              <TextField
                label={translate('manage.createStudent.email')}
                fullWidth
                clearable={false}
                autoComplete={false}
                error={!!fieldState.error}
                helperText={fieldState.error ? translate(fieldState.error.message ?? '') : null}
                {...field}
              />
            )}
          />
        </Stack>
      </Dialog>
      {showUnsavedWarning && (
        <ConfirmationDialog
          title={translate('common.unsavedChangesTitle')}
          question={translate('common.unsavedChangesQuestion')}
          cancelButton={translate('common.cancel')}
          acceptButton={translate('common.leavePage')}
          onCancel={() => setShowUnsavedWarning(false)}
          onAccept={handleClose}
        />
      )}
    </>
  );
};
